import { __awaiter } from "tslib";
import logger, { LogCategory } from "../../util/logger";
/** Represents an action executable by the program execution */
export default class Action {
    constructor() {
        /** A list of this action's conditions. All conditions must evaluate to true for this action to be executed */
        this._conditions = [];
        this._name = "Action";
        this._endsStep = false;
        this._endsInstruction = false;
        this._sideffects = [];
        this._conditions = [];
        this._waits_before = [];
        this._waits_after = [];
    }
    /**
     * Adds a condition to this action execution.
     * Before this action is executed, this (and all the eventual other) conditions are avaluated.
     * All the conditions must return true for this action to be executed
     * @param conditions
     * @returns The same action this method was called on. Allows method concatenation
     */
    condition(...conditions) {
        this._conditions.push(...conditions);
        return this;
    }
    /**
     * Marks this action as the last action of a microstep
     * @returns The same action this method was called on. Allows method concatenation
     */
    endstep() {
        this._endsStep = true;
        return this;
    }
    /**
     * Marks this action as the last action of an instruction
     * @returns The same action this method was called on. Allows method concatenation
     */
    endinstruction() {
        this._endsInstruction = true;
        return this;
    }
    /**
     * Adds sideffects to this action.
     * A sideffect is another action that will be executed at the same time as this action.
     * If this action's condition evaluates to false neither this action nor its sideffects will be executed
     * @param sideffects
     * @returns The same action this method was called on. Allows method concatenation
     */
    sideffects(...sideffects) {
        this._sideffects.push(...sideffects);
        return this;
    }
    /**
     * Add waits that will be awaited before this action execution.
     * A wait is fundamentally a promise that can be awaited
     * @param waits
     * @returns The same action this method was called on. Allows method concatenation
     */
    waitFor(...waits) {
        this._waits_before.push(...waits);
        return this;
    }
    /**
     * Add waits that will be awaited after this action execution.
     * This action will be considered executed only after all its waits have resolved
     * A wait is fundamentally a promise that can be awaited
     * @param waits
     * @returns The same action this method was called on. Allows method concatenation
     */
    thenWaitFor(...waits) {
        this._waits_after.push(...waits);
        return this;
    }
    /**
     * Executes this action
     * @param ctx - The object that contains all of the components needed by an action to execute (ex. cpu, ram, etc.)
     * @returns A promise that resolves when this action has ended its execution
     */
    execute(ctx) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this._conditions.reduceRight((finalVal, condition) => finalVal && condition(ctx), true)) {
                logger.debug(`Skipping: ${this.toString()}`, LogCategory.EXECUTION);
                return;
            }
            logger.debug(`Executing: ${this.toString()}`, LogCategory.EXECUTION);
            yield Promise.all(this._waits_before.map(wait => wait(ctx)));
            yield Promise.all([...this._sideffects.map(sideffect => sideffect.execute(ctx)), this.action(ctx)]);
            return Promise.all(this._waits_after.map(wait => wait(ctx)));
        });
    }
    /**
     * Returns true if this action is marked as the last action of a microstep
     * @returns
     */
    doesEndStep() {
        return this._endsStep;
    }
    /**
     * Returns true if this action is marked as the last action of an instruction
     * @returns
     */
    doesEndInstruction() {
        return this._endsInstruction;
    }
    toString() {
        return `${this._name}`;
    }
}
